import {
  AlertModal,
  AlertModalType,
  Button,
  Flex,
  FlexColumn,
  SecondaryButton,
  SelectInput,
  useAsyncFunctionProgress,
} from '@equifinance/shared';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import useApplicationApi from '../../../api/useApplicationApi';
import { ApplicationStageBackward, useApplicationsContext } from '../../../contexts/applications';

const ChangeStatusModalContainer = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[8]};
`}`;

const ChangeStatusModalButtonContainer = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[2]};
  justify-content: end;
`}`;

function ChangeStatusModalContent({ close, applicationId }: { close: () => void, applicationId: string }) {
  const { getApplicationChangeStatusOptions } = useApplicationApi();
  const { changeApplicationStatus } = useApplicationsContext();
  const [status, setStatus] = useState<ApplicationStageBackward | ''>('');
  const [statusOptions, setStatusOptions] = useState<Record<ApplicationStageBackward, string> | {}>({});

  useEffect(() => {
    if (!applicationId) return;

    (async () => {
      const statusOptions = await getApplicationChangeStatusOptions(applicationId);
      const options = statusOptions?.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {} as Record<ApplicationStageBackward, string>);
      setStatusOptions(options ?? {});
    })();
  }, [applicationId]);

  const onConfirm = async () => {
    await changeApplicationStatus(applicationId, status as ApplicationStageBackward);
    close();
  };

  const { inProgress, execute: executeConfirm } = useAsyncFunctionProgress(onConfirm);

  return (
    <ChangeStatusModalContainer>
      <SelectInput
        id="application-new-status"
        placeholder="Select"
        label="New status"
        value={status}
        onChange={(value: any) => setStatus(value as ApplicationStageBackward)}
        options={statusOptions}
        required
      />
      <ChangeStatusModalButtonContainer>
        <SecondaryButton small onClick={close}>Cancel</SecondaryButton>
        <Button disabled={!status || inProgress} small onClick={executeConfirm}>Confirm</Button>
      </ChangeStatusModalButtonContainer>
    </ChangeStatusModalContainer>
  );
}
export type ChangeStatusModalProps = Omit<AlertModalType, 'content'> & {
  applicationId: string,
  onClose: () => void;
}

export default function ChangeStatusModal({
  applicationId,
  onClose,
  ...props
}: ChangeStatusModalProps) {
  return (
    <AlertModal
      {...props}
      title="Change application status"
      content={(
        <ChangeStatusModalContent
          applicationId={applicationId}
          close={onClose}
        />
      )}
      onClose={onClose}
      showButtons={false}
    />
  );
}
